/**
 * Client auth state. The session itself is an HttpOnly cookie; this context
 * tracks *who* is signed in (via /api/auth/me) and exposes login helpers.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, ApiError } from "./api";
import type { Admin, AdminRole, Passkey } from "./types";

interface AuthState {
  admin: Admin | null;
  passkeys: Passkey[];
  loading: boolean;
  loginWithPasskey: (email: string) => Promise<void>;
  loginWithTotp: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasRole: (required: AdminRole) => boolean;
}

const ROLE_RANK: Record<AdminRole, number> = { viewer: 0, admin: 1, super_admin: 2 };

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<{ admin: Admin; passkeys: Passkey[] }>("/auth/me");
      setAdmin(res.admin);
      setPasskeys(res.passkeys);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setAdmin(null);
        setPasskeys([]);
      } else {
        throw err;
      }
    }
  }, []);

  useEffect(() => {
    refresh()
      .catch(() => setAdmin(null))
      .finally(() => setLoading(false));
  }, [refresh]);

  const loginWithPasskey = useCallback(
    async (email: string) => {
      const { challengeId, options } = await api.post<{
        challengeId: string;
        options: Parameters<typeof startAuthentication>[0]["optionsJSON"];
      }>("/auth/login/options", { email });
      const assertion = await startAuthentication({ optionsJSON: options });
      await api.post("/auth/login/verify", { challengeId, response: assertion });
      await refresh();
    },
    [refresh],
  );

  const loginWithTotp = useCallback(
    async (email: string, code: string) => {
      await api.post("/auth/login/totp", { email, code });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setAdmin(null);
    setPasskeys([]);
  }, []);

  const hasRole = useCallback(
    (required: AdminRole) => admin !== null && ROLE_RANK[admin.role] >= ROLE_RANK[required],
    [admin],
  );

  return (
    <AuthContext.Provider
      value={{ admin, passkeys, loading, loginWithPasskey, loginWithTotp, logout, refresh, hasRole }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
